<template>
  <RouterView v-if="isLoginRoute" />
  <div v-else class="app" :class="{ 'menu-open': menuOpen }">
    <!-- 모바일 상단 바 (≤768px 에서만 표시) -->
    <header class="topbar">
      <button class="topbar-toggle" type="button" @click="menuOpen = !menuOpen"
              :aria-label="menuOpen ? 'Close menu' : 'Open menu'" :aria-expanded="menuOpen">
        <span aria-hidden="true">{{ menuOpen ? '✕' : '☰' }}</span>
      </button>
      <span class="topbar-title">🎮 romset-manager</span>
      <span v-if="runningJobs.length > 0" class="running-pill topbar-pill" :title="`${runningJobs.length} running`">
        <span class="running-dot"></span>{{ runningJobs.length }}
      </span>
    </header>

    <!-- 모바일 사이드바 오버레이 (메뉴 열렸을 때 본문 클릭으로 닫기) -->
    <div v-if="menuOpen" class="sidebar-backdrop" @click="menuOpen = false"></div>

    <aside class="sidebar"
           :class="{ 'is-open': menuOpen, resizing: resizing, collapsed: sidebarCollapsed }"
           :style="{ '--sidebar-w': sidebarCollapsed ? '64px' : (sidebarWidth + 'px') }">
      <!-- 데스크톱(≥769px) collapse/expand 토글 — 사이드바 상단 anchor.
           클릭으로 메뉴 폭 64px(아이콘만) ↔ 사용자 폭 토글, localStorage 영속. -->
      <button class="sidebar-collapse-btn"
              @click="toggleSidebarCollapse"
              :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
              :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'">
        {{ sidebarCollapsed ? '»' : '«' }}
      </button>
      <header class="brand">
        <span class="logo">🎮</span>
        <div class="brand-text">
          <h1>romset-manager</h1>
          <span class="tag">Randit Software</span>
        </div>
      </header>
      <nav @click="menuOpen = false">
        <RouterLink to="/dashboard" :title="sidebarCollapsed ? 'Dashboard' : ''"><span class="ico">🏠</span><span class="label"> Dashboard</span></RouterLink>
        <RouterLink to="/platform" :title="sidebarCollapsed ? 'Platform' : ''"><span class="ico">🕹️</span><span class="label"> Platform</span></RouterLink>
        <RouterLink to="/catalog" :title="sidebarCollapsed ? 'Catalog' : ''"><span class="ico">📚</span><span class="label"> Catalog</span></RouterLink>
        <RouterLink to="/inventory" :title="sidebarCollapsed ? 'Inventory' : ''"><span class="ico">📦</span><span class="label"> Inventory</span></RouterLink>
        <RouterLink to="/bios" :title="sidebarCollapsed ? 'BIOS' : ''"><span class="ico">💾</span><span class="label"> BIOS</span></RouterLink>
        <RouterLink to="/acquisition" :title="sidebarCollapsed ? 'Acquisition' : ''">
          <span class="ico">🔍</span><span class="label"> Acquisition</span>
          <span v-if="runningJobs.length > 0" class="running-pill" :title="`${runningJobs.length} running`">
            <span class="running-dot"></span>{{ runningJobs.length }}
          </span>
        </RouterLink>
        <RouterLink to="/drive" :title="sidebarCollapsed ? 'Drive' : ''"><span class="ico">☁️</span><span class="label"> Drive</span></RouterLink>
      </nav>

      <!-- 글로벌 active acquisition 배너 (있으면) -->
      <div v-if="runningJobs.length > 0" class="sidebar-activity">
        <div class="sa-title">
          <span class="sa-spinner"></span>
          <strong>Searching…</strong>
        </div>
        <div v-for="j in runningJobs.slice(0, 3)" :key="j.id" class="sa-job">
          <div class="sa-job-line">
            <span class="sa-id">#{{ j.id }}</span>
            <span class="sa-pct">{{ Math.min(100, j.progressPct ?? 0).toFixed(0) }}%</span>
          </div>
          <div class="sa-bar">
            <div class="sa-bar-fill" :style="{ width: Math.min(100, j.progressPct ?? 0) + '%' }"></div>
          </div>
          <div class="sa-job-meta">
            ok {{ j.downloadsOk ?? 0 }} / fail {{ j.downloadsFailed ?? 0 }}
            <span v-if="j.etaSec != null"> · {{ formatEta(j.etaSec) }}</span>
          </div>
        </div>
        <div v-if="runningJobs.length > 3" class="sa-more">+ {{ runningJobs.length - 3 }} more</div>
      </div>
      <footer class="footer" v-if="loggedIn">
        <button class="btn logout-btn" @click="logout"
                :title="sidebarCollapsed ? 'Logout' : ''">
          <span class="ico">🚪</span><span class="label"> Logout</span>
        </button>
      </footer>
      <!-- 리사이즈 anchor — 데스크톱(≥769px)에서 드래그로 사이드바 폭 조정.
           더블클릭 시 기본값(240) 리셋. 모바일에서는 CSS 로 숨김 (drawer 모드). -->
      <div class="sidebar-resizer"
           role="separator" aria-orientation="vertical" aria-label="Resize sidebar"
           @mousedown.prevent="startResize"
           @touchstart.prevent="startResize"
           @dblclick="resetSidebarWidth"></div>
    </aside>
    <main class="content">
      <RouterView />
    </main>
  </div>
  <ConfirmDialog />
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { formatEta } from '@/utils/format'
import { useRoute, useRouter } from 'vue-router'
import { clearTokens, isLoggedIn } from '@/api/auth'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import {
  runningJobs,
  startGlobalAcquisitionPoll,
  stopGlobalAcquisitionPoll,
} from '@/composables/acquisitionStatus'

const route = useRoute()
const router = useRouter()
const isLoginRoute = computed(() => route.path === '/login')
const loggedIn = ref(isLoggedIn())
const menuOpen = ref(false)

// ---- 사이드바 리사이즈 (데스크톱 anchor) ----
const SIDEBAR_DEFAULT = 240
const SIDEBAR_MIN = 180
const SIDEBAR_MAX = 480
const SIDEBAR_LS_KEY = 'romset.sidebar.width'

function _readStoredWidth(): number {
  try {
    const v = parseInt(localStorage.getItem(SIDEBAR_LS_KEY) || '', 10)
    if (!isNaN(v) && v >= SIDEBAR_MIN && v <= SIDEBAR_MAX) return v
  } catch { /* localStorage 없음/차단 */ }
  return SIDEBAR_DEFAULT
}
const sidebarWidth = ref<number>(_readStoredWidth())
const resizing = ref(false)

function startResize(ev: MouseEvent | TouchEvent) {
  // 모바일(≤768px) 에서는 anchor 가 CSS 로 숨겨져 있어 거의 호출되지 않지만
  // 안전 가드: 좁은 화면이면 무시.
  if (window.innerWidth < 769) return
  resizing.value = true
  const move = (e: MouseEvent | TouchEvent) => {
    const x = (e as TouchEvent).touches?.[0]?.clientX ?? (e as MouseEvent).clientX
    if (typeof x !== 'number') return
    const w = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(x)))
    sidebarWidth.value = w
  }
  const stop = () => {
    resizing.value = false
    try { localStorage.setItem(SIDEBAR_LS_KEY, String(sidebarWidth.value)) } catch { /* ignore */ }
    window.removeEventListener('mousemove', move)
    window.removeEventListener('mouseup', stop)
    window.removeEventListener('touchmove', move)
    window.removeEventListener('touchend', stop)
    window.removeEventListener('touchcancel', stop)
  }
  window.addEventListener('mousemove', move)
  window.addEventListener('mouseup', stop)
  window.addEventListener('touchmove', move, { passive: false })
  window.addEventListener('touchend', stop)
  window.addEventListener('touchcancel', stop)
}

function resetSidebarWidth() {
  sidebarWidth.value = SIDEBAR_DEFAULT
  try { localStorage.setItem(SIDEBAR_LS_KEY, String(SIDEBAR_DEFAULT)) } catch { /* ignore */ }
}

// 사이드바 collapse — 데스크톱(≥769px)에서만 의미. 모바일은 drawer 모드라 무시.
const SIDEBAR_COLLAPSED_LS = 'romset.sidebar.collapsed'
const sidebarCollapsed = ref<boolean>((() => {
  try { return localStorage.getItem(SIDEBAR_COLLAPSED_LS) === '1' } catch { return false }
})())
function toggleSidebarCollapse() {
  sidebarCollapsed.value = !sidebarCollapsed.value
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_LS, sidebarCollapsed.value ? '1' : '0')
  } catch { /* ignore */ }
}

router.afterEach(() => { loggedIn.value = isLoggedIn(); menuOpen.value = false })

function logout() {
  clearTokens()
  loggedIn.value = false
  stopGlobalAcquisitionPoll()
  router.replace({ path: '/login' })
}

onMounted(() => {
  if (isLoggedIn()) startGlobalAcquisitionPoll()
})
onUnmounted(stopGlobalAcquisitionPoll)
// 로그인 직후에도 폴링 시작
router.afterEach((to) => {
  if (to.path !== '/login' && isLoggedIn()) startGlobalAcquisitionPoll()
})
</script>

<style scoped>
.app { display: flex; min-height: 100vh; min-height: 100dvh; align-items: stretch; }
/* 데스크톱에서는 topbar / backdrop 숨김 (사이드바가 항상 보임) */
.topbar { display: none; }
.sidebar-backdrop { display: none; }
.sidebar {
  /* --sidebar-w 는 인라인 :style 로 런타임 주입. fallback 240px. */
  width: var(--sidebar-w, 240px);
  flex-shrink: 0;
  background: linear-gradient(180deg, #1e1e2a 0%, #171720 100%);
  color: #e8e8ef;
  padding: 1.25rem 1rem;
  display: flex; flex-direction: column;
  box-shadow: var(--sh-md);
  /* sticky + viewport 높이 — 페이지가 길어져도 사이드바는 항상 화면 전체. */
  position: sticky;
  top: 0;
  height: 100vh; height: 100dvh;
  overflow: hidden;  /* 내부 nav 만 스크롤 */
}
/* sidebar collapse 토글 버튼 — 사이드바 상단 우측 모서리. 데스크톱 전용. */
.sidebar-collapse-btn {
  position: absolute; top: 8px; right: 8px;
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,0.08);
  color: #f5f5fa;
  border: 1px solid #4a4a5c;
  border-radius: var(--r-md);
  font-size: 14px; font-weight: 700;
  cursor: pointer;
  z-index: 60;
  transition: background var(--t-fast);
}
.sidebar-collapse-btn:hover { background: rgba(55, 85, 209, 0.45); border-color: var(--c-primary); }

/* 사이드바 width 변화 transition — collapse/expand 부드럽게. 드래그 중엔 disable. */
.sidebar:not(.resizing) { transition: width 0.2s ease-out; }

/* collapsed 상태: 64px 좁힘. 텍스트 (.label, .brand-text, h1, .tag) + 활동
   배너 + 리사이즈 anchor 모두 숨김. icon 만 표시. */
.sidebar.collapsed { padding: 1.25rem 0.5rem; }
.sidebar.collapsed .brand-text { display: none; }
.sidebar.collapsed .brand { justify-content: center; }
.sidebar.collapsed .label { display: none; }
.sidebar.collapsed nav a {
  justify-content: center;
  padding: .5rem;
}
.sidebar.collapsed nav a .ico { width: 1.5em; font-size: 1.2em; }
.sidebar.collapsed .running-pill {
  position: absolute; top: 4px; right: 4px;
  font-size: 0.6rem; padding: 0 4px;
}
.sidebar.collapsed nav a { position: relative; }
.sidebar.collapsed .sidebar-activity { display: none; }
.sidebar.collapsed .logout-btn {
  padding: 0.5rem;
  justify-content: center;
}
.sidebar.collapsed .logout-btn .label { display: none; }
.sidebar.collapsed .sidebar-resizer { display: none; }
.sidebar.collapsed .sidebar-collapse-btn {
  position: relative; top: 0; right: 0; margin: 0 auto 1rem;
}

/* 리사이즈 anchor — 사이드바 우측 가장자리 6px 영역. 호버/드래그 시 강조. */
.sidebar-resizer {
  position: absolute; top: 0; right: -3px; bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 50;
  background: transparent;
  transition: background var(--t-fast);
}
.sidebar-resizer:hover { background: rgba(55, 85, 209, 0.35); }
.sidebar.resizing .sidebar-resizer { background: rgba(55, 85, 209, 0.55); }
.sidebar.resizing { user-select: none; }
/* 드래그 중 본문 user-select 도 막음 */
.app:has(.sidebar.resizing) { user-select: none; cursor: col-resize; }
/* nav 가 길어지면 nav 만 스크롤 — sidebar-activity + footer 는 하단 고정 */
.sidebar nav { overflow-y: auto; min-height: 0; }
.brand {
  display: flex; align-items: center; gap: .75rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #2d2d3a;
  margin-bottom: 1rem;
}
.brand .logo { font-size: 1.8rem; }
.brand h1 { font-size: .95rem; margin: 0; color: #fff; letter-spacing: -0.01em; }
.brand .tag { font-size: .7rem; color: #8a8a9e; }

.sidebar nav { display: flex; flex-direction: column; gap: .2rem; flex: 1; }
.sidebar a {
  color: #c7c7d3;
  text-decoration: none;
  padding: .5rem .7rem;
  border-radius: var(--r-md);
  display: flex; align-items: center; gap: .6rem;
  font-size: .92rem;
  transition: background var(--t-fast), color var(--t-fast);
}
.sidebar a:hover { background: rgba(255,255,255,0.05); color: #fff; }
.sidebar a .ico { font-size: 1rem; width: 1.2em; text-align: center; }
.sidebar a.router-link-active {
  background: var(--c-primary);
  color: #fff;
  font-weight: 500;
}
.footer { padding-top: .8rem; border-top: 1px solid #2d2d3a; }
/* 사이드바가 항상 다크 배경이라 일반 .btn 의 --c-text(밝기 선택형) 색상으로는 잘 안 보임 → 고정색 강제 */
.sidebar .logout-btn {
  width: 100%;
  justify-content: center;
  background: rgba(255,255,255,0.08) !important;
  color: #f5f5fa !important;
  border: 1px solid #4a4a5c !important;
  font-weight: 600;
}
.sidebar .logout-btn:hover:not(:disabled) {
  background: rgba(239,68,68,0.18) !important;
  border-color: #ef4444 !important;
  color: #fca5a5 !important;
}
.content { flex: 1; padding: 1.75rem 2rem; background: var(--c-bg); overflow: auto; }

/* Global acquisition indicator in sidebar */
.running-pill {
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 4px;
  background: #eab308; color: #1f1408;
  font-size: 0.68rem; font-weight: 700;
  padding: 1px 6px; border-radius: 9999px;
}
.running-dot {
  width: 6px; height: 6px; border-radius: 50%; background: #1f1408;
  animation: sa-pulse 1.2s ease-in-out infinite;
}
.sidebar-activity {
  margin-top: .6rem; padding: .6rem .7rem;
  background: rgba(234, 179, 8, 0.1);
  border: 1px solid rgba(234, 179, 8, 0.35);
  border-radius: var(--r-md);
  font-size: 0.72rem; color: #f5d47a;
  animation: sa-pulse 2s ease-in-out infinite;
}
.sa-title { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.sa-spinner {
  width: 14px; height: 14px;
  border: 2px solid #eab308; border-top-color: transparent;
  border-radius: 50%; animation: sa-spin 0.8s linear infinite;
}
.sa-job { margin-top: 6px; padding-top: 6px; border-top: 1px dashed rgba(234,179,8,0.3); }
.sa-job:first-of-type { border-top: none; padding-top: 0; margin-top: 0; }
.sa-job-line { display: flex; justify-content: space-between; }
.sa-id { font-family: var(--ff-mono); color: #e8c774; }
.sa-pct { font-weight: 700; color: #f5d47a; }
.sa-bar {
  margin: 3px 0;
  height: 4px; background: rgba(234,179,8,0.2); border-radius: 2px; overflow: hidden;
}
.sa-bar-fill {
  height: 100%; background: #eab308;
  transition: width 0.4s ease-out;
}
.sa-job-meta { color: #cfa65a; font-size: 0.65rem; }
.sa-more { margin-top: 4px; text-align: center; color: #cfa65a; }
@keyframes sa-spin { to { transform: rotate(360deg); } }
@keyframes sa-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.75; } }

/* ================================================================
 * Mobile (≤ 768px) — 햄버거 토글 사이드바 + 상단바
 * ================================================================ */
@media (max-width: 768px) {
  .app { flex-direction: column; }

  /* 상단바 노출 */
  .topbar {
    display: flex; align-items: center; gap: 0.75rem;
    padding: 0.6rem 0.9rem;
    padding-top: max(0.6rem, env(safe-area-inset-top));
    background: linear-gradient(180deg, #1e1e2a 0%, #171720 100%);
    color: #f5f5fa;
    box-shadow: var(--sh-sm);
    position: sticky; top: 0; z-index: 30;
  }
  .topbar-toggle {
    width: 44px; height: 44px;
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.08);
    color: #f5f5fa;
    border: 1px solid #4a4a5c;
    border-radius: var(--r-md);
    font-size: 1.2rem; font-weight: 700;
    cursor: pointer;
  }
  .topbar-title { font-weight: 600; flex: 1; font-size: 1rem; }
  .topbar-pill { margin-left: 0; }

  /* 사이드바 — 슬라이드 드로어 (모바일) */
  .sidebar {
    position: fixed; inset: 0 auto 0 0;
    width: 280px; max-width: 80vw;
    height: auto;            /* fixed inset 으로 자동 풀 높이 — 데스크톱 100dvh 오버라이드 */
    overflow: hidden;        /* 내부 nav 만 스크롤 */
    transform: translateX(-100%);
    transition: transform 0.25s ease-out;
    z-index: 40;
    padding-top: max(1.25rem, env(safe-area-inset-top));
    padding-bottom: max(1.25rem, env(safe-area-inset-bottom));
  }
  .sidebar.is-open { transform: translateX(0); }
  /* 모바일은 drawer 모드 — anchor + collapse 토글 숨김 (햄버거가 표준 인터랙션).
     collapsed 상태 클래스가 있어도 모바일은 width:280px drawer 그대로 (collapse 무시). */
  .sidebar-resizer { display: none; }
  .sidebar-collapse-btn { display: none; }
  .sidebar.collapsed { padding: 1.25rem 1rem; width: 280px !important; max-width: 80vw !important; }
  .sidebar.collapsed .brand-text,
  .sidebar.collapsed .label,
  .sidebar.collapsed .sidebar-activity,
  .sidebar.collapsed .logout-btn .label { display: revert; }
  .sidebar.collapsed nav a { justify-content: flex-start; padding: 0.75rem 0.9rem; }

  /* 백드롭 — 메뉴 열렸을 때 본문 어둡게 + 클릭 닫기 */
  .sidebar-backdrop {
    display: block;
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.45);
    z-index: 35;
    animation: sb-fade-in 0.2s ease-out;
  }
  @keyframes sb-fade-in { from { opacity: 0; } to { opacity: 1; } }

  /* 사이드바 메뉴 항목 — 터치 타깃 ↑ */
  .sidebar a { padding: 0.75rem 0.9rem; min-height: 44px; }

  .content { padding: 0.9rem 0.75rem; }
}
</style>
